import { clampAccentHex, parseHex6 } from '@/lib/color-utils';
import { pageBackgroundTheme } from '@/lib/page-background';
import { resolvePageFont, type PageFontId } from '@/lib/page-fonts';

/**
 * The appearance settings a seller may attach to a page they own.
 *
 * Declared HERE rather than imported from the product context because two
 * different documents now carry this exact map: `products/{id}.design` and
 * `users/{uid}.design` (the seller's storefront brand). `ProductDesign` in
 * src/context/product-context.tsx is the same shape and stays where it is, so
 * no product import site moves; it is assignable to this.
 *
 * Keeping the declaration in the lib also means this module has no import from
 * a `.tsx`, which is what lets the type-stripping node test runner load it.
 */
export type PageDesign = {
  enabled?: boolean;
  starfield?: boolean;
  accentBackground?: boolean;
  /** `#RRGGBB`, or absent/'' for no accent (supa.place's ink buttons and links). */
  accentColor?: string;
  /**
   * 'light' | 'dark' to force one, or absent/'' to keep the viewer's own.
   * Ignored while `backgroundColor` is set: the colour decides then.
   */
  theme?: 'light' | 'dark' | '';
  /**
   * `#RRGGBB` for a solid page background in place of the plain one, or
   * absent/'' for the plain page in each visitor's own light or dark mode.
   * Painted exactly as picked; the page's text, panels and light or dark mode
   * follow it so everything stays readable (src/lib/page-background.ts). Left
   * off the stored map while unset (see withoutUnsetDesignKeys).
   */
  backgroundColor?: string;
  /**
   * `#RRGGBB` for the accent background's own colour, or absent/'' to follow
   * the default: the viewer's own pick if they made one, else the page's
   * accent. (The accent background only renders in dark mode.) Stored
   * separately from `accentColor` so the two can differ; the editor holds ''
   * until the seller explicitly turns a custom gradient colour on, so an unset
   * one keeps following the accent rather than freezing today's value. An
   * unset one is left off the stored map (see withoutUnsetDesignKeys).
   */
  gradientColor?: string;
  /**
   * Which edge the accent background rises from: 'bottom' or 'top', or
   * absent/'' to keep the viewer's own (whose default is from below).
   */
  gradientDirection?: 'top' | 'bottom' | '';
  /**
   * The seller's fonts for this page: a catalogue id from src/lib/page-fonts.ts,
   * or absent/'' for the site font (Inter). Unlike everything above these
   * restyle the page's own content rather than the app shell, so they are not
   * part of DesignOverride: resolvePageFonts reads them and the page applies
   * them itself (src/lib/page-font-faces.ts). Left off the stored map while
   * unset, like the gradient keys (see withoutUnsetDesignKeys).
   */
  headingFont?: string;
  bodyFont?: string;
  /**
   * The saved design this page follows, '1' to '5', or absent/'' when it
   * follows none (src/lib/design-presets.ts). A linked page still carries every
   * value of its preset in the keys above, so nothing that renders a page reads
   * this: the functions keep the copies in step when the seller edits the
   * preset (functions/src/lib/design-presets.ts). Meaningful only while
   * `enabled` is true, and written only then.
   */
  preset?: string;
};

/**
 * Every key a design map may hold, in one list, for the cross-tree drift test.
 *
 * The same closed set is written out twice more, where this module cannot be
 * imported: the hasOnly() list in designShapeWithinBounds (firestore.rules),
 * which DENIES a write carrying any other key, and DESIGN_KEYS in
 * functions/src/lib/user-field-bounds.ts, which DELETES any other key from
 * users/{uid}.design after the fact. src/lib/product-designability.test.ts
 * reads both and fails when either differs from this.
 *
 * Built from a Record over `keyof PageDesign` so the compiler, not a comment,
 * keeps it complete: a key added to the type and not here (or the reverse) is a
 * type error.
 */
const PAGE_DESIGN_KEY_SET: Record<keyof PageDesign, true> = {
  enabled: true,
  starfield: true,
  accentBackground: true,
  accentColor: true,
  theme: true,
  backgroundColor: true,
  gradientColor: true,
  gradientDirection: true,
  headingFont: true,
  bodyFont: true,
  preset: true,
};
export const PAGE_DESIGN_KEYS = Object.keys(PAGE_DESIGN_KEY_SET) as ReadonlyArray<keyof PageDesign>;

/**
 * The keys a design map leaves off while they are '' (unset). See
 * withoutUnsetDesignKeys for why.
 */
const OPTIONAL_DESIGN_KEYS = [
  'backgroundColor', 'gradientColor', 'gradientDirection', 'headingFont', 'bodyFont', 'preset',
] as const;

/**
 * A design map as it should be WRITTEN: the same map minus every optional key
 * (the background colour, the gradient's colour and direction, the two fonts,
 * the preset link) while it is still '' (unset), and minus the preset link on
 * a design that is not applied. Every client write of a design map goes
 * through this: the profile Design card, and updateProduct / addProduct in
 * src/context/product-context.tsx for both product forms.
 *
 * WHY AN UNSET KEY STAYS OFF THE DOCUMENT. Each of these keys joined a closed
 * key list that lives in three places shipped by three separate deploys: the
 * hasOnly() list in firestore.rules and DESIGN_KEYS in the functions repair go
 * out by hand, while App Hosting rolls this app out on every push to main. A
 * write carrying a key the LIVE rules do not name is denied whole (the product
 * or profile save, not just the key), and a key the LIVE repair does not name
 * is deleted from users/{uid}.design afterwards. Writing '' for every seller
 * who never picked a gradient, a font or a saved design would make every design
 * save depend on that deploy order. Leaving it out costs nothing: an absent key
 * reads exactly as '' in resolveDesignOverride, resolvePageFonts, the forms and
 * the pickers, and the map is replaced whole on write, so leaving a key out
 * still clears a value stored earlier. Only a seller who actually uses one of
 * them depends on the new rules and functions being live.
 *
 * WHY THE PRESET LINK NEEDS `enabled`. A product whose "Customise this product
 * page" switch is off renders its seller's profile design, whatever its own map
 * says, so a link there would follow a preset onto a page that never shows it.
 * The form keeps the link in its own state, so switching back on restores it;
 * only the write drops it.
 *
 * `accentColor: ''` and `theme: ''` have always been written and every
 * deployed ruleset accepts them, so they round-trip as they always have.
 * Anything that is not a plain object (a deleteField() sentinel, say) is
 * returned untouched, because rebuilding one would change the write.
 */
export function withoutUnsetDesignKeys<T extends PageDesign | null | undefined>(design: T): T {
  if (!design || typeof design !== 'object') return design;
  const proto = Object.getPrototypeOf(design);
  if (proto !== Object.prototype && proto !== null) return design;
  const out: PageDesign = { ...design };
  for (const key of OPTIONAL_DESIGN_KEYS) {
    if (out[key] === '') delete out[key];
  }
  if (out.enabled !== true) delete out.preset;
  return out as T;
}

/**
 * Whether saving `formDesign` changes the design on a product as stored, which
 * is what firestore.rules diffs a write against. The form side is put in the
 * form a save writes it (withoutUnsetDesignKeys, with undefined values dropped,
 * as updateProduct does); the stored side is compared exactly as it is, so a map
 * saved before unset keys were left off (it still holds '' for them) counts as
 * changed, because writing it without them is a change. Key order is ignored.
 * No design in the save writes nothing, so changes nothing. The product editor
 * uses it to keep a changed design off its big write (see saveChanges in the
 * edit page).
 */
export function designWriteChanges(formDesign: PageDesign | null | undefined, storedDesign: unknown): boolean {
  if (!formDesign || typeof formDesign !== 'object') return false;
  const entriesOf = (map: unknown): string => {
    if (!map || typeof map !== 'object' || Array.isArray(map)) return 'none';
    const record = map as Record<string, unknown>;
    return JSON.stringify(Object.keys(record).filter((key) => record[key] !== undefined).sort()
      .map((key) => [key, record[key]]));
  };
  return entriesOf(withoutUnsetDesignKeys(formDesign)) !== entriesOf(storedDesign);
}

/**
 * What a product page publishes to the app shell while it is open.
 *
 * `null` means "no override — use the viewer's own settings". A non-null value
 * fully replaces the background effects for the current page (both booleans are
 * explicit once a seller has enabled customisation).
 *
 * A SELLER'S page never publishes null since 2026-10-19: a product page or
 * storefront whose seller set nothing publishes PLAIN_SELLER_PAGE instead (see
 * sellerPageOverride), so the visitor's own sky never decides how a seller's
 * page looks. null now only reaches the shell from pages that are not a
 * seller's, and from an editor whose preview is off.
 *
 * WHY THIS IS A LIB AND NOT PART OF THE CONTEXT. `resolveDesignOverride` is
 * pure: no React, no DOM, no Firestore. It is also the single point where a
 * stored hex becomes a colour the buyer sees, which makes it the one piece of
 * feature 3 most worth a test — and nothing that lives in a `.tsx` can be
 * loaded by the type-stripping node test runner this repo uses. The context
 * re-exports both names, so every existing import site is unchanged.
 */
export type DesignOverride = {
  starfield: boolean;
  accentBackground: boolean;
  /**
   * The accent to paint while this page is open, ALREADY CLAMPED, or null to
   * keep the viewer's own. Clamped here rather than at the point of use so
   * there is exactly one place a stored hex becomes a rendered colour: the
   * buyer's page and the seller's in-editor preview both read this, so neither
   * can show a colour the other would not.
   */
  accentColor: string | null;
  /**
   * Force the page light or dark for as long as it is open, or null to keep
   * the viewer's own setting.
   *
   * ThemeProvider folds this into `resolvedTheme`, which is what every consumer
   * already reads to answer "what theme is this page in right now" — the
   * `<html>` class, the starfield and gradient gating in ClientLayout, and the
   * appearance handed to Stripe Elements. One effective value rather than a
   * second parallel notion of the theme is what keeps those from disagreeing.
   *
   * With a `backgroundColor` this is the colour's own mode (light text on a
   * dark colour, dark on a light one; pageBackgroundTheme), whatever the stored
   * `theme` says: the colour and the mode its text needs are one setting.
   */
  theme: 'light' | 'dark' | null;
  /**
   * The page's solid background, a validated lowercase `#rrggbb`, or null for
   * the plain page. ThemeProvider paints it, with panels and text to match
   * (pageBackgroundPalette in src/lib/page-background.ts), while the page is
   * open.
   *
   * VALIDATED BUT NOT CLAMPED, like the gradient colour, and for the opposite
   * reason to the accent: the text on it is what moves (the theme above and
   * the palette), never the colour the seller picked.
   */
  backgroundColor: string | null;
  /**
   * The accent background's colour, a validated lowercase `#rrggbb`, or null
   * to follow the default (ThemeProvider resolves it: the viewer's own pick,
   * else the accent this page paints).
   *
   * VALIDATED BUT NOT CLAMPED, unlike `accentColor`. The accent is clamped
   * because `--primary` is a SURFACE that button labels are painted on. This
   * colour never carries text: it is a background tint at a fraction of its
   * alpha, fading into black, so there is no contrast to protect and
   * darkening it would only hand the seller a muddier colour than they picked.
   * It still has to be a real hex, because it ends up in a CSS custom property.
   */
  gradientColor: string | null;
  /** 'top' or 'bottom' to set the edge the gradient rises from, or null for the viewer's own. */
  gradientDirection: 'top' | 'bottom' | null;
} | null;

/**
 * Collapse a product's stored design preferences into a layout override.
 * Returns `null` (inherit the viewer's theme) unless the seller has explicitly
 * enabled customisation for the product.
 */
export function resolveDesignOverride(design: PageDesign | undefined | null): DesignOverride {
  if (!design || !design.enabled) return null;
  // Format only, like the gradient colour: a malformed value means the plain
  // page rather than reaching a CSS custom property.
  const backgroundColor = parseHex6(design.backgroundColor);
  // Validated against the two legal values rather than passed through. It ends
  // up as a class on <html>, and a stored document is not a trusted source: a
  // legacy or hand-edited value has to mean "no override" instead of putting an
  // arbitrary string into the classList. A background colour decides instead
  // (see DesignOverride.theme).
  const theme = backgroundColor
    ? pageBackgroundTheme(backgroundColor)
    : design.theme === 'light' || design.theme === 'dark' ? design.theme : null;
  return {
    starfield: !!design.starfield,
    accentBackground: !!design.accentBackground,
    // clampAccentHex also rejects anything that is not a 6-digit hex, so a
    // malformed value on a legacy or hand-edited document means no accent
    // rather than reaching document.documentElement.style.
    accentColor: clampAccentHex(design.accentColor),
    theme,
    backgroundColor,
    // Format only (see DesignOverride): '' and anything malformed mean "follow
    // the default", exactly as they do for the accent.
    gradientColor: parseHex6(design.gradientColor),
    // Pinned to the two values the renderer has a class string for, for the
    // same reason as `theme` above.
    gradientDirection: design.gradientDirection === 'top' || design.gradientDirection === 'bottom'
      ? design.gradientDirection
      : null,
  };
}

/**
 * The override a PRODUCT page publishes, given the product's own design and the
 * seller's storefront brand (`users/{uid}.design`).
 *
 * PRECEDENCE. A product that has customisation switched on wins outright: it is
 * the more specific setting, and the seller set it knowing the page it applies
 * to. Only when the product says nothing does the seller's brand apply, which is
 * what stops a seller having to re-pick their colour once per listing.
 *
 * WHY THE BRAND'S THEME IS DROPPED. The product's own design arrives with the
 * product document, so its forced light/dark is applied on first paint. The
 * seller's brand does not: it comes from a second read of `users/{sellerId}`
 * that resolves a moment later, so a forced theme inherited from it would
 * repaint the whole page after the buyer has already started reading it. The
 * accent and the two background effects are recolours of a page that is already
 * the right theme, so they settle without that. A seller who wants a product
 * page pinned to one theme sets it on the product, where it is free.
 *
 * The gradient's colour and direction cascade with the rest. They restyle the
 * accent background, which already cascades, and like it they change a layer
 * behind the content rather than the page's theme. Only `theme` is withheld.
 * No field merge here either: a product with its own design and no gradient
 * colour follows its own default, not the brand's gradient colour.
 *
 * EXCEPT WITH A BACKGROUND COLOUR. The brand's colour cascades like its accent,
 * and its theme then comes with it, because that theme is not a separate
 * choice: it is the light or dark text the colour needs to stay readable
 * (DesignOverride.theme). The colour without it would put dark text on a dark
 * page. The repaint the note above avoids happens anyway when the colour lands,
 * so withholding the theme would buy nothing.
 *
 * @param {PageDesign | undefined | null} own The product's own design map.
 * @param {PageDesign | undefined | null} sellerBrand The seller's `users/{uid}.design`.
 * @return {DesignOverride} What the page should publish to the shell.
 */
export function resolveDesignWithSellerBrand(
  own: PageDesign | undefined | null,
  sellerBrand: PageDesign | undefined | null,
): DesignOverride {
  const product = resolveDesignOverride(own);
  if (product) return product;
  const brand = resolveDesignOverride(sellerBrand);
  if (!brand) return null;
  return brand.backgroundColor ? brand : { ...brand, theme: null };
}

/**
 * What a seller's page shows when its design (and, for a product, the
 * seller's brand) sets nothing: the plain page, with no stars, planets or
 * gradient, in the visitor's own light or dark mode and accent.
 *
 * Owner decision 2026-10-19: a product page or storefront is the seller's
 * page, so the cosmic sky is something a seller switches on, not something the
 * visitor's own settings paint behind every seller's products. Before this a
 * seller page with no design published null, and the shell drew each
 * visitor's own sky (then on by default in dark mode).
 */
export const PLAIN_SELLER_PAGE: NonNullable<DesignOverride> = Object.freeze({
  starfield: false,
  accentBackground: false,
  accentColor: null,
  theme: null,
  backgroundColor: null,
  gradientColor: null,
  gradientDirection: null,
});

/**
 * The override a seller's page (a product page, a storefront, or an editor's
 * preview of one) publishes: its resolved design, or PLAIN_SELLER_PAGE when
 * that sets nothing. Never null, so the visitor's own background effects never
 * apply on a seller's page.
 */
export function sellerPageOverride(override: DesignOverride): NonNullable<DesignOverride> {
  return override ?? PLAIN_SELLER_PAGE;
}

/** The two fonts a page renders its seller's content in. null is the site font. */
export type PageFonts = { heading: PageFontId | null; body: PageFontId | null };

/**
 * The fonts a page renders in, given its own design and, for a product page,
 * the seller's storefront brand (`users/{uid}.design`). The storefront passes
 * its own design and no brand.
 *
 * THE SAME PRECEDENCE AS resolveDesignWithSellerBrand, and for the same
 * reasons: a page whose own design is applied wins outright, and only a page
 * that sets nothing inherits the brand. No field merge, so a product with its
 * own design and no fonts is in the site font, not the brand's.
 *
 * UNLIKE THE THEME, THE BRAND'S FONTS DO CASCADE. The theme is withheld because
 * the brand used to reach the product page a moment after it, and a forced
 * light/dark from it repainted a page the buyer was already reading. Fonts are
 * read from the server's copy of the brand (sellerSeed.design, built by
 * sellerSummaryFromDoc), so they are in the first HTML and change nothing
 * after it.
 *
 * Every id passes through resolvePageFont, so an unknown id, or a heading-only
 * face stored as the body font, renders in the site font.
 */
export function resolvePageFonts(
  own: PageDesign | undefined | null,
  sellerBrand?: PageDesign | undefined | null,
): PageFonts {
  const source = own?.enabled ? own : sellerBrand?.enabled ? sellerBrand : null;
  return {
    heading: resolvePageFont(source?.headingFont, 'heading'),
    body: resolvePageFont(source?.bodyFont, 'body'),
  };
}
